import { EntryCollection, createClient } from 'contentful'
import dayjs from 'dayjs'
import 'dotenv/config'
import { Entry } from '~/types/contentful/contentful'
import {
  IArticleContent,
  IEntry,
  IFileDownload,
  IFooter,
  INavigationBar,
  IPage,
  ISiteWideSettings,
} from '~/types/generated/contentful'

import { CONTENTFUL_ENVIRONMENT } from '../utils/consts'

const space = process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID!
const enviroment = CONTENTFUL_ENVIRONMENT
const accessToken = process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN!
const previewToken = process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN!

export const getClient = (preview?: boolean) => {
  return createClient({
    space: space,
    accessToken: preview ? previewToken : accessToken,
    host: preview ? 'preview.contentful.com' : undefined,
    environment: enviroment,
  })
}

export const getContentful = async () => {
  return await getClient()?.getEntries({
    include: 2,
  })
}

export const fetchEntryById = async (id: string): Promise<Entry<any>> => {
  try {
    return await getClient()?.getEntry(id)
  } catch (error) {
    console.error('Error fetching entry: ', error)
    throw error
  }
}

export const getOldestArticleYear = async (): Promise<number> => {
  const entries = await getClient()?.getEntries({
    content_type: 'articleContent',
    order: 'fields.dateOfArticle', // Sorting articles by date in ascending order
    limit: 1, // Fetching only the oldest article
  })

  const entry = entries?.items[0] as IArticleContent

  if (entries && entries.items.length > 0) {
    const oldestArticleDate = entry.fields.dateOfArticle
    return dayjs(oldestArticleDate).year()
  }

  return dayjs().year()
}

export async function fetchPagesByQuery(
  query: string,
  page: number = 1, // Default to the first page
): Promise<{
  items: IPage[]
  count: number
}> {
  const limit = 10 // Number of items per page
  const skip = (page - 1) * limit // Calculate the number of items to skip

  const entries = await getClient()?.getEntries<IPage>({
    content_type: 'page',
    query: query,
    include: 1,
    limit: limit,
    skip: skip, // Add skip to the query
  })

  const response = { items: entries.items, count: entries.total }

  return response as unknown as {
    items: IPage[]
    count: number
  }
}

export const fetchPageById = async (id: string): Promise<IPage | null> => {
  const entries: EntryCollection<IPage> = await getClient()?.getEntries({
    content_type: 'page',
    'sys.id': id,
    include: 10,
  })

  if (entries.items.length > 0) {
    return entries.items[0] as unknown as IPage
  }

  return null
}

export const fetchAllNavPages = async (preview?: boolean): Promise<IPage[]> => {
  const entries = await getClient(preview)?.getEntries<IPage>({
    content_type: 'page',
    'fields.parent[exists]': false,
    include: 3,
  })

  return entries.items as unknown as IPage[]
}

let navBarCache: INavigationBar[] = []

export const fetchNavBar = async (
  preview?: boolean,
): Promise<INavigationBar[]> => {
  if (navBarCache.length) {
    return navBarCache
  }

  const entries = await getClient(preview)?.getEntries<INavigationBar>({
    content_type: 'navigationBar',
    include: 5,
  })

  navBarCache = entries.items as unknown as INavigationBar[]

  return navBarCache
}

let footerCache: IFooter[] = []

export const fetchFooter = async (preview?: boolean): Promise<IFooter[]> => {
  if (footerCache.length) {
    return footerCache
  }

  const entries = await getClient(preview)?.getEntries<IFooter>({
    content_type: 'footer',
    include: 10,
  })

  footerCache = entries.items as unknown as IFooter[]

  return footerCache
}

let siteWideSettingsCache: ISiteWideSettings[] = []

export const fetchSiteWideSettings = async (
  preview?: boolean,
): Promise<ISiteWideSettings[]> => {
  if (siteWideSettingsCache.length) {
    return siteWideSettingsCache
  }

  const entries = await getClient(preview)?.getEntries<ISiteWideSettings>({
    content_type: 'siteWideSettings',
    include: 5,
  })

  siteWideSettingsCache = entries.items as unknown as ISiteWideSettings[]

  return siteWideSettingsCache
}

export const fetchContentItems = async (
  contentTypeId: string,
  preview?: boolean,
) => {
  const entries = await getClient(preview)
    .getEntries({
      content_type: contentTypeId,
      include: 10,
    })
    .then(response => response.items)
    .catch(console.error)
  return entries as unknown as IEntry[]
}

export const fetchContentTypes = async () => {
  const entries = await getClient()
    .getContentTypes()
    .then(response => response.items)
    .catch(console.error)

  return entries
}

export const getFileLink = async (id: string) => {
  const entries = await getClient().getEntries({
    content_type: 'fileDownload',
    'fields.id': id,
  })
  const file = entries.items[0]

  return file as unknown as IFileDownload
}
